/**
 * Agenda · camada de acesso a dados (repositório local-first, Dexie).
 *
 * Solo-first: `garantirClinicaSolo` cria, na 1ª ativação, uma "clínica de 1" com um
 * único membro (o dono, admin+profissional). Tudo modela MULTI, mas roda SOLO.
 * Pensado p/ `useLiveQuery`. Escritas carimbam `atualizado_em`.
 */
import { db } from "../schema.dexie";
import { getConfig, salvarConfig } from "../db/queries";
import type { EspecialidadeProf } from "../types";
import type {
  Agendamento,
  Clinica,
  Disponibilidade,
  LocalClinica,
  MembroClinica,
  Modalidade,
  PapelClinica,
  StatusAgendamento,
  TipoConsulta,
} from "./types";

const novoId = () => crypto.randomUUID();
const agoraISO = () => new Date().toISOString();

// ── Clínica (solo-first) ─────────────────────────────────────────────────────

export const getClinica = (id: string) => db.clinica.get(id);

/** A clínica ativa (via config.clinica_id), se houver. */
export async function getClinicaAtiva(): Promise<Clinica | undefined> {
  const cfg = await getConfig();
  if (!cfg?.clinica_id) return undefined;
  return db.clinica.get(cfg.clinica_id);
}

/**
 * Garante a "clínica de 1": cria clínica + membro dono na 1ª vez e grava
 * `config.clinica_id`. Idempotente — se já existe, só retorna. Não liga a agenda
 * (isso é `ativarAgenda`).
 */
export async function garantirClinicaSolo(donoNome?: string): Promise<Clinica> {
  const cfg = await getConfig();
  if (cfg?.clinica_id) {
    const existente = await db.clinica.get(cfg.clinica_id);
    if (existente) return existente;
  }

  const nome = donoNome?.trim();
  const clinica: Clinica = {
    id: novoId(),
    nome: nome ? `clínica de ${nome.split(/\s+/)[0]}` : "minha clínica",
    dono_nome: nome || null,
    solo: true,
    criado_em: agoraISO(),
  };
  await db.clinica.add(clinica);

  const dono: MembroClinica = {
    id: novoId(),
    clinica_id: clinica.id,
    nome: nome || "eu",
    papel: "admin",
    especialidade: null,
    registro_classe: null,
    cor: null,
    arquivado: false,
    criado_em: agoraISO(),
  };
  await db.membro_clinica.add(dono);

  await salvarConfig({ clinica_id: clinica.id });
  return clinica;
}

/** Liga a agenda (garante a clínica solo). Card 🗓️ passa a aparecer no home. */
export async function ativarAgenda(donoNome?: string): Promise<Clinica> {
  const clinica = await garantirClinicaSolo(donoNome);
  await salvarConfig({ agenda_ativa: true });
  return clinica;
}

/** Desliga a agenda. Os dados ficam guardados; só somem da navegação. */
export async function desativarAgenda(): Promise<void> {
  await salvarConfig({ agenda_ativa: false });
}

export const editarClinica = (id: string, patch: Partial<Clinica>) =>
  db.clinica.update(id, patch);

// ── Membros (profissionais / secretaria) ─────────────────────────────────────

export const listarMembros = (clinicaId: string): Promise<MembroClinica[]> =>
  db.membro_clinica
    .where("clinica_id")
    .equals(clinicaId)
    .filter((m) => !m.arquivado)
    .toArray();

export const getMembro = (id: string) => db.membro_clinica.get(id);

export type NovoMembroInput = {
  clinica_id: string;
  nome: string;
  papel: PapelClinica;
  especialidade?: EspecialidadeProf | null;
  registro_classe?: string | null;
  cor?: string | null;
};

export async function criarMembro(input: NovoMembroInput): Promise<string> {
  const membro: MembroClinica = {
    id: novoId(),
    clinica_id: input.clinica_id,
    nome: input.nome.trim(),
    papel: input.papel,
    especialidade: input.especialidade ?? null,
    registro_classe: input.registro_classe?.trim() || null,
    cor: input.cor ?? null,
    arquivado: false,
    criado_em: agoraISO(),
  };
  await db.membro_clinica.add(membro);
  // ao adicionar um colega/secretária, a clínica deixa de ser "de 1" (revela o multi).
  if (input.papel !== "admin") {
    const clinica = await db.clinica.get(input.clinica_id);
    if (clinica?.solo) await db.clinica.update(input.clinica_id, { solo: false });
  }
  return membro.id;
}

export const editarMembro = (id: string, patch: Partial<MembroClinica>) =>
  db.membro_clinica.update(id, patch);

export const arquivarMembro = (id: string) =>
  db.membro_clinica.update(id, { arquivado: true });

// ── Locais (consultórios) ────────────────────────────────────────────────────

export const listarLocais = (clinicaId: string): Promise<LocalClinica[]> =>
  db.local_clinica
    .where("clinica_id")
    .equals(clinicaId)
    .filter((l) => !l.arquivado)
    .toArray();

export async function criarLocal(
  clinicaId: string,
  nome: string,
  endereco?: string | null,
): Promise<string> {
  const local: LocalClinica = {
    id: novoId(),
    clinica_id: clinicaId,
    nome: nome.trim(),
    endereco: endereco?.trim() || null,
    arquivado: false,
    criado_em: agoraISO(),
  };
  await db.local_clinica.add(local);
  return local.id;
}

export const arquivarLocal = (id: string) =>
  db.local_clinica.update(id, { arquivado: true });

// ── Disponibilidade (janelas recorrentes — provedor nativo) ──────────────────

export const listarDisponibilidade = (clinicaId: string): Promise<Disponibilidade[]> =>
  db.disponibilidade.where("clinica_id").equals(clinicaId).toArray();

export async function criarDisponibilidade(
  d: Omit<Disponibilidade, "id" | "criado_em">,
): Promise<string> {
  const reg: Disponibilidade = { ...d, id: novoId(), criado_em: agoraISO() };
  await db.disponibilidade.add(reg);
  return reg.id;
}

export const excluirDisponibilidade = (id: string) => db.disponibilidade.delete(id);

// ── Agendamentos ─────────────────────────────────────────────────────────────

export const getAgendamento = (id: string) => db.agendamento.get(id);

/** Consultas de um dia (chave "YYYY-MM-DD"), cronológicas. */
export const agendamentosDoDia = (
  clinicaId: string,
  chave: string,
): Promise<Agendamento[]> =>
  db.agendamento
    .where("[clinica_id+data]")
    .equals([clinicaId, chave])
    .toArray();

/** Consultas num intervalo de dias [deChave, ateChave] — alimenta a grade do mês. */
export const agendamentosNoIntervalo = (
  clinicaId: string,
  deChave: string,
  ateChave: string,
): Promise<Agendamento[]> =>
  db.agendamento
    .where("[clinica_id+data]")
    .between([clinicaId, deChave], [clinicaId, ateChave])
    .toArray();

export type NovoAgendamentoInput = {
  clinica_id: string;
  profissional_id: string;
  paciente_id?: string | null;
  paciente_nome: string;
  paciente_contato?: string | null;
  data: string; // "YYYY-MM-DD"
  hora_inicio: string; // "HH:MM"
  duracao_min?: number;
  tipo?: TipoConsulta;
  modalidade?: Modalidade;
  local_id?: string | null;
  link_online?: string | null;
  observacao_agenda?: string | null;
};

export async function criarAgendamento(input: NovoAgendamentoInput): Promise<string> {
  const agora = agoraISO();
  const ag: Agendamento = {
    id: novoId(),
    clinica_id: input.clinica_id,
    profissional_id: input.profissional_id,
    paciente_id: input.paciente_id ?? null,
    paciente_nome: input.paciente_nome.trim(),
    paciente_contato: input.paciente_contato?.trim() || null,
    data: input.data,
    hora_inicio: input.hora_inicio,
    duracao_min: input.duracao_min ?? 50,
    tipo: input.tipo ?? "sessao",
    modalidade: input.modalidade ?? "presencial",
    local_id: input.local_id ?? null,
    link_online: input.link_online?.trim() || null,
    status: "agendado",
    observacao_agenda: input.observacao_agenda?.trim() || null,
    provedor: "nativo",
    criado_em: agora,
    atualizado_em: agora,
  };
  await db.agendamento.add(ag);
  return ag.id;
}

export async function editarAgendamento(
  id: string,
  patch: Partial<Agendamento>,
): Promise<void> {
  await db.agendamento.update(id, { ...patch, atualizado_em: agoraISO() });
}

export async function mudarStatus(id: string, status: StatusAgendamento): Promise<void> {
  await db.agendamento.update(id, { status, atualizado_em: agoraISO() });
}

/**
 * Remarcar: marca o atual como `remarcado` e cria um novo `agendado` na nova data/hora,
 * preservando paciente/profissional/tipo/etc. Retorna o id do novo agendamento.
 */
export async function remarcar(
  id: string,
  novaData: string,
  novaHora: string,
): Promise<string | null> {
  const atual = await db.agendamento.get(id);
  if (!atual) return null;
  await mudarStatus(id, "remarcado");
  return criarAgendamento({
    clinica_id: atual.clinica_id,
    profissional_id: atual.profissional_id,
    paciente_id: atual.paciente_id,
    paciente_nome: atual.paciente_nome,
    paciente_contato: atual.paciente_contato,
    data: novaData,
    hora_inicio: novaHora,
    duracao_min: atual.duracao_min,
    tipo: atual.tipo,
    modalidade: atual.modalidade,
    local_id: atual.local_id,
    link_online: atual.link_online,
    observacao_agenda: atual.observacao_agenda,
  });
}

export const excluirAgendamento = (id: string) => db.agendamento.delete(id);
